<template>
  <span
    class="inline-block font-semibold text-shade-900 first-letter:uppercase"
  >
    {{ label }}
  </span>
  <div class="group relative font-normal">
    <div v-for="option in filteredOptions" :key="displayFn(option)"></div>
    <Combobox v-model="selected" :multiple="multiple" v-slot="{ open }">
      <ComboboxButton ref="comboButton" />
      <ComboboxInput
        :style="{ paddingLeft: `${padding}px` }"
        class="relative w-full cursor-pointer border border-shade-900 transition-colors placeholder:capitalize placeholder:text-shade-alpha-900 hover:border-primary-400 focus:border-primary-400 focus:ring focus:ring-primary-alpha-400 focus-visible:outline-none"
        @change="query = $event.target.value"
        :displayValue="() => query"
        @click="handleComboState(open)"
        @focus="handleComboState(open)"
      />
      <div
        v-for="(selected, index) in selected"
        :key="index"
        :style="{
          left: `${index * 155}px`,
          minWidth: '150px',
          maxWidth: '150px',
          whiteSpace: 'nowrap',
          zIndex: '10',
        }"
        class="m-2 absolute top-0 flex items-center justify-center gap-2 bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-800 hover:bg-gray-300"
      >
        <span>{{ shorten(displayFn(selected)) }}</span>

        <button
          class="flex h-4 w-4 items-center justify-center rounded-full bg-shade-900 transition-colors duration-200 ease-in-out hover:bg-shade-700"
          type="reset"
          @click.prevent="clearSearchInput(index)"
        >
          <IconCross class="h-3 w-3 text-shade-50" />
        </button>
      </div>

      <ComboboxOptions
        class="absolute z-20 mt-1 flex max-h-60 w-2/3 flex-col overflow-y-scroll border border-shade-900 bg-shade-50 p-3 shadow-sm"
      >
        <ComboboxOption
          v-for="option in filteredOptions"
          :key="displayFn(option)"
          v-slot="{ selected, active }"
          :value="option"
        >
          <li
            class="relative cursor-pointer select-none py-2.5 pl-8 pr-6"
            :class="{
              'bg-primary-alpha-100 text-primary-400': selected,
              'bg-shade-alpha-300': active && !selected,
            }"
          >
            <span
              class="block truncate"
              :class="{ 'font-medium': selected, 'font-normal': !selected }"
            >
              {{ displayFn(option) }}
            </span>
            <span
              v-if="selected"
              class="absolute inset-y-0 left-0 flex items-center pl-2"
              :class="{
                'text-primary-400': selected,
                'text-shade-900': active && !selected,
              }"
            >
              <IconCheck class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </Combobox>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import IconCheck from '~/assets/icon/general/Check.svg'
import IconCross from '~/assets/icon/general/Cross.svg'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
  ComboboxButton,
} from '@headlessui/vue'

const query = ref('')
const comboButton = ref(null)

const handleComboState = (open) => {
  if (!open) {
    comboButton.value?.$el?.click()
  }
}
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  data: {
    type: Object,
    required: true,
  },
  model: {
    type: String,
    required: true,
  },
  multiple: {
    type: Boolean,
    required: false,
    default: true,
  },
  label: {
    type: String,
    required: false,
  },
  displayFn: Function,
  getOption: Function,
})

const selected = ref(props.data[props.model])

const selectedNames = computed(() => {
  const name = props.displayFn(selected.value)
  if (selected.value) {
    if (!props.multiple) {
      return [name]
    }
    return selected.value
      .filter((option) => option)
      .map((option) => {
        return name
      })
  }
  return []
})
const padding = ref(10)

const filteredOptions = computed(() => {
  if (query.value === '') {
    return props.options
  }

  const searchTerm = normalize(
    query.value.replace(selectedNames.value.join(', '), '')
  )

  return props.options.filter((option) =>
    normalize(props.displayFn(option).trim().toLowerCase()).includes(
      searchTerm.toLowerCase()
    )
  )
})
watch(
  () => props.data[props.model],
  (newData) => {
    if (props.multiple) {
      if (newData?.length !== selected.value?.length) {
        selected.value = newData?.map((datum) =>
          props.options.find((option) => props.getOption(option) === datum)
        )
      }
    } else {
      selected.value = props.options.find(
        (option) => props.getOption(option) === newData
      )
    }
  },
  { deep: true }
)
watch(
  () => props.data[props.model],
  (newData) => {
    // console.log(newData)
  },
  { deep: true }
)
watch(
  selected,
  (newSelected) => {
    query.value = ''
    padding.value = 10
    if (!props.multiple) {
      props.data[props.model] = props.getOption(newSelected)
    } else {
      props.data[props.model] = newSelected?.map((option) =>
        props.getOption(option)
      )
    }
    if (newSelected) {
      padding.value = padding.value + newSelected.length * 155
    } else {
      padding.value = 0
    }
  },
  { deep: true }
)
const clearSearchInput = (index) => {
  selected.value.splice(index, 1)
  query.value = ''
  props.data[props.model] = null
}
</script>
